import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  IconRefresh,
  IconTrendingUp,
  IconTrendingDown,
  IconCash,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useAgentCashFlow } from "@/hooks/use-agent-analytics";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/analytics/")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AnalyticsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AnalyticsContent() {
  const [period, setPeriod] = useState<"today" | "week" | "month" | "all">("month");
  const { data: cashFlow, isLoading, refetch } = useAgentCashFlow({ period });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            View your transaction analytics and cash flow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={period === "today" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("today")}
          >
            Today
          </Button>
          <Button
            variant={period === "week" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("week")}
          >
            This Week
          </Button>
          <Button
            variant={period === "month" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("month")}
          >
            This Month
          </Button>
          <Button
            variant={period === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod("all")}
          >
            All Time
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <IconRefresh className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Cash Flow Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deposits (In)</CardTitle>
            <IconTrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-9 w-32 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-green-600">
                  ৳{cashFlow?.deposits?.total_volume?.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {cashFlow?.deposits?.count || 0} deposits received
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Payouts (Out)</CardTitle>
            <IconTrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-9 w-32 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className="text-2xl font-bold text-red-600">
                  ৳{cashFlow?.payouts?.total_volume?.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {cashFlow?.payouts?.count || 0} payouts sent
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Flow</CardTitle>
            <IconCash className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-9 w-32 animate-pulse bg-muted rounded" />
            ) : (
              <>
                <div className={`text-2xl font-bold ${(cashFlow?.net_flow || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                  ৳{cashFlow?.net_flow?.toLocaleString() || "0"}
                </div>
                <p className="text-xs text-muted-foreground">
                  Net cash flow
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* By SIM */}
      {cashFlow?.by_sim && cashFlow.by_sim.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Cash Flow by SIM</CardTitle>
            <CardDescription>
              Transaction volume breakdown per SIM card
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {cashFlow.by_sim.map((sim: any) => (
                <div key={sim.sim_id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium font-mono">{sim.phone_number}</p>
                    <p className="text-sm text-muted-foreground capitalize">{sim.operator}</p>
                  </div>
                  <div className="text-right space-y-1">
                    <div className="flex items-center gap-4 justify-end">
                      <div>
                        <p className="text-sm text-muted-foreground">Deposits</p>
                        <p className="font-bold text-green-600">
                          ৳{sim.deposits_volume?.toLocaleString() || "0"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sim.deposits_count || 0} txns
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Payouts</p>
                        <p className="font-bold text-red-600">
                          ৳{sim.payouts_volume?.toLocaleString() || "0"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sim.payouts_count || 0} txns
                        </p>
                      </div>
                    </div>
                    <p className={`text-sm font-medium ${(sim.net_flow || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      Net: ৳{sim.net_flow?.toLocaleString() || "0"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
